import { TimeSince } from "./Utilities/relativeTime";
import {
  formatUserDateTimeWithZone,
  parseUtcTimestampToMs,
} from "./Utilities/timezone";
import { useResolvedDark } from "../../ThemeProvider/ThemeContext";

const clampImpactScore = (value) => {
  if (value == null || value === "") return null;

  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) return null;

  return Math.max(-1, Math.min(1, parsedValue));
};

const getImpactTone = (impactScore, isDark) => {
  const clampedScore = clampImpactScore(impactScore);
  if (clampedScore == null) return null;

  const hue = ((clampedScore + 1) / 2) * 120;
  const label =
    clampedScore > 0.12
      ? "Supportive"
      : clampedScore < -0.12
        ? "Negative"
        : "Neutral";

  return {
    background: `linear-gradient(180deg, ${
      isDark
        ? `hsla(${hue}, 82%, 58%, 0.15)`
        : `hsla(${hue}, 88%, 42%, 0.09)`
    } 0%, ${isDark ? "#12172A" : "#FFFFFF"} 100%)`,
    border: isDark
      ? `hsla(${hue}, 85%, 62%, 0.42)`
      : `hsla(${hue}, 88%, 38%, 0.22)`,
    accent: isDark
      ? `hsl(${hue}, 82%, 66%)`
      : `hsl(${hue}, 88%, 34%)`,
    badgeBg: isDark
      ? `hsla(${hue}, 80%, 60%, 0.16)`
      : `hsla(${hue}, 88%, 42%, 0.10)`,
    badgeBorder: isDark
      ? `hsla(${hue}, 84%, 68%, 0.38)`
      : `hsla(${hue}, 88%, 34%, 0.18)`,
    label,
    scoreText: `${clampedScore > 0 ? "+" : ""}${clampedScore.toFixed(2)}`,
  };
};

const getCountdownTime = (startTime) => {
  const targetTimeMs = parseUtcTimestampToMs(startTime);
  if (!Number.isFinite(targetTimeMs)) {
    return {
      isPast: false,
      timeString: "-",
    };
  }

  const timeDiff = targetTimeMs - Date.now();

  const getTimeString = (diff) => {
    const absDiff = Math.abs(diff);
    const hours = Math.floor(absDiff / (1000 * 60 * 60));
    const minutes = Math.floor((absDiff % (1000 * 60 * 60)) / (1000 * 60));
    return hours > 0
      ? `${hours} hours ${minutes} minutes`
      : `${minutes} minutes`;
  };

  return {
    isPast: timeDiff <= 0,
    timeString: getTimeString(timeDiff),
  };
};

export const InsightTile = ({
  title,
  description,
  timeStamp,
  type,
  startTime,
  isDark: isDarkProp,
  impactScore,
  question,
  onAskQuestion,
}) => {
  const isDark = useResolvedDark(isDarkProp);
  const impactTone =
    type === "count_down" ? null : getImpactTone(impactScore, isDark);
  const normalizedQuestion =
    typeof question === "string" && question.trim() ? question.trim() : null;
  const showQuestionCta =
    type !== "count_down" &&
    normalizedQuestion &&
    typeof onAskQuestion === "function";

  const countdownInfo =
    type === "count_down" ? getCountdownTime(startTime) : null;
  const isPast = countdownInfo?.isPast || false;
  const timeRemaining = countdownInfo?.timeString || "";

  const theme = isDark
    ? {
        surface: "#12172A",
        border: "#232B4A",
        text: "#E8ECF8",
        muted: "#A6B0CF",
        cdBg: "rgba(125,160,255,0.14)",
        cdBorder: "rgba(125,160,255,0.35)",
        cdText: "#E8ECF8",
        divider: "rgba(166,176,207,0.18)",
        questionBg: "rgba(11,15,26,0.34)",
      }
    : {
        surface: "#FFFFFF",
        border: "#E9ECEF",
        text: "#0B1220",
        muted: "#5B6472",
        cdBg: "rgba(13,110,253,0.10)",
        cdBorder: "rgba(13,110,253,0.22)",
        cdText: "#0B1220",
        divider: "rgba(91,100,114,0.12)",
        questionBg: "#F8FAFC",
      };

  const isActiveCountdown = type === "count_down" && !isPast;

  return (
    <div
      className="card mb-3"
      role="article"
      aria-labelledby="insight-title"
      aria-describedby="insight-description"
      style={{
        borderRadius: 14,
        background: isActiveCountdown
          ? theme.cdBg
          : impactTone?.background ?? theme.surface,
        padding: 15,
        border: `1px solid ${
          isActiveCountdown
            ? theme.cdBorder
            : impactTone?.border ?? theme.border
        }`,
        boxShadow: impactTone ? `inset 4px 0 0 ${impactTone.accent}` : "none",
      }}
    >
      <div className="card-body" style={{ padding: 0 }}>
        <div className="d-flex justify-content-between align-items-start gap-3">
          <div style={{ fontWeight: 700, fontSize: "1.05rem", flex: 1 }}>
            <h5
              id="insight-title"
              className="card-title mb-2"
              style={{ color: isActiveCountdown ? theme.cdText : theme.text }}
            >
              {type === "count_down"
                ? isPast
                  ? `${title?.toUpperCase()} Released`
                  : `Countdown For ${title?.toUpperCase()}`
                : title || "No title"}
            </h5>
          </div>

          {(timeStamp || impactTone) && (
            <div
              className="d-flex flex-column align-items-end gap-2 text-end"
              style={{ minWidth: "118px" }}
            >
              {timeStamp ? (
                <p
                  className="card-text small mb-0"
                  style={{ fontSize: "0.75rem", color: theme.muted }}
                >
                  <TimeSince timestamp={timeStamp} />
                </p>
              ) : null}

              {impactTone ? (
                <div
                  style={{
                    borderRadius: 999,
                    padding: "5px 10px",
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    color: impactTone.accent,
                    background: impactTone.badgeBg,
                    border: `1px solid ${impactTone.badgeBorder}`,
                    whiteSpace: "nowrap",
                  }}
                >
                  {impactTone.label} {impactTone.scoreText}
                </div>
              ) : null}
            </div>
          )}
        </div>

        <p
          id="insight-description"
          className="card-text mt-2"
          style={{
            fontSize: "0.98rem",
            lineHeight: 1.5,
            color: theme.muted,
          }}
        >
          {type === "count_down" ? (
            isPast ? (
              <>
                <strong style={{ color: theme.text }}>
                  {title?.toUpperCase()}
                </strong>{" "}
                data was released at{" "}
                <strong style={{ color: theme.text }}>
                  {getLocalTime(startTime)}
                </strong>
              </>
            ) : (
              <>
                Data for{" "}
                <strong style={{ color: theme.text }}>
                  {title?.toUpperCase()}
                </strong>{" "}
                to be released in{" "}
                <strong style={{ color: theme.text }}>{timeRemaining}</strong>
              </>
            )
          ) : (
            description || "No description available."
          )}
        </p>

        {showQuestionCta ? (
          <div
            className="mt-3 pt-3"
            style={{
              borderTop: `1px solid ${
                impactTone?.badgeBorder || theme.divider
              }`,
            }}
          >
            <div
              className="mb-2"
              style={{
                fontSize: "0.72rem",
                fontWeight: 700,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: impactTone?.accent ?? theme.muted,
              }}
            >
              Continue in chat?
            </div>
            <button
              type="button"
              onClick={() => onAskQuestion(normalizedQuestion)}
              style={{
                width: "100%",
                textAlign: "left",
                borderRadius: 12,
                border: `1px solid ${
                  impactTone?.badgeBorder || theme.divider
                }`,
                background: theme.questionBg,
                color: theme.text,
                padding: "12px 14px",
                fontSize: "0.94rem",
                lineHeight: 1.5,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {normalizedQuestion}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export const getLocalTime = (utcTime) => {
  return formatUserDateTimeWithZone(utcTime);
};
